import { useEffect, useMemo, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import clsx from 'clsx'

import { cumulativeAssessmentAdminApi } from '@/api/cumulativeAssessmentAdmin'
import { coachingKeys } from '@/hooks/coaching/useCoachingToday'
import { useCumulativeAssessmentDetail } from '@/hooks/coaching/useCumulativeAssessmentDetail'
import type { CumulativeAssessmentDetailView, CumulativeSegmentHistoryView, MathHeadRunSummaryView } from '@/types/coaching'
import { getApiErrorMessage } from '@/utils/apiError'
import { scopeRangesByCourse } from '@/utils/cumulativeScope'
import type { CourseNameLabels } from '@/utils/courseName'
import { ConfirmDialog } from './ConfirmDialog'
import { TestCoachingDetail } from './TestCoachingDetail'

interface Props {
  learnerId: number
  assessmentId: number | null
}

const KST_FORMAT = new Intl.DateTimeFormat('ko-KR', {
  timeZone: 'Asia/Seoul',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
})

export function formatKst(iso: string | null | undefined): string {
  if (!iso) return '—'
  const date = new Date(iso)
  return Number.isNaN(date.getTime()) ? '—' : KST_FORMAT.format(date)
}

const TERMINAL_STATUSES = ['COMPLETED', 'EXEMPTED']

/**
 * 시험범위 문구 — `코어 A1 챕터 1~3`, 과정 경계를 넘으면 `코어 C 챕터 17~18 · 어드밴스드 챕터 1`.
 * 챕터 번호는 과정 안에서 1부터 센 번호다(전역 통번호를 쓰면 16챕터짜리 어드밴스드가 "챕터 19"가 된다).
 * 과정 정보가 없는 응답은 기존 `1~3챕터` 문구로 떨어진다.
 */
function scopeText(
  assessment: CumulativeAssessmentDetailView['assessment'],
  t: (key: string, options?: Record<string, unknown>) => string,
): string {
  const courseNames: CourseNameLabels = {
    core: (level) => t('cumulative.course.core', { level }),
    advanced: (level) => (level ? t('cumulative.course.advancedLevel', { level }) : t('cumulative.course.advanced')),
    elite: () => t('cumulative.course.elite'),
  }
  const ranges = scopeRangesByCourse(assessment.scopeChapters, courseNames)
  if (ranges.length === 0) {
    return t('cumulative.scope', {
      start: assessment.scopeStartChapterOrdinal,
      end: assessment.scopeEndChapterOrdinal,
    })
  }
  return ranges
    .map((range) => {
      const chapters = range.start === range.end
        ? t('cumulative.scopeChapter', { chapter: range.start })
        : t('cumulative.scopeRange', { start: range.start, end: range.end })
      return [range.courseName, chapters].filter(Boolean).join(' ')
    })
    .join(' · ')
}

/** 남은 일이 있는 세그먼트를 먼저 연다. 둘 다 끝났으면 레슨 테스트로 돌아간다. */
function defaultSegmentType(segments: CumulativeSegmentHistoryView[]): string | null {
  const pending = segments.find((segment) => !TERMINAL_STATUSES.includes(segment.status))
  return pending?.type ?? segments[0]?.type ?? null
}

export function CumulativeAssessmentCoachingDetail({ learnerId, assessmentId }: Props) {
  const { t } = useTranslation('coaching')
  const queryClient = useQueryClient()
  const [activeType, setActiveType] = useState<string | null>(null)
  const [resolveOpen, setResolveOpen] = useState(false)
  const [resolving, setResolving] = useState(false)
  const [resolveError, setResolveError] = useState<string | null>(null)

  const query = useCumulativeAssessmentDetail(learnerId, assessmentId)
  const detail = query.data ?? null

  const load = async () => {
    if (assessmentId == null) return
    await query.refetch()
  }

  /**
   * 누적 테스트 마감. 두 세그먼트가 끝나도 자동으로 닫히지 않으므로, 리포트 발행과 다음 레슨 개방은
   * 교사가 여기를 누르는 시점에 일어난다. 되돌릴 수 없어 확인 다이얼로그를 거친다.
   */
  const resolve = async () => {
    if (assessmentId == null) return
    setResolving(true)
    setResolveError(null)
    try {
      const resolved = await cumulativeAssessmentAdminApi.resolve(learnerId, assessmentId)
      queryClient.setQueryData(coachingKeys.cumulativeAssessment(learnerId, assessmentId), resolved)
      setResolveOpen(false)
    } catch (err) {
      setResolveError(getApiErrorMessage(err, t))
    } finally {
      setResolving(false)
    }
  }

  // 탭 선택은 기억하지 않는다. 원생이 바뀌면 남은 일도 바뀌므로 그 원생의 미완료 세그먼트로 다시 연다.
  useEffect(() => { setActiveType(null) }, [learnerId, assessmentId])

  const segments = detail?.segments ?? []
  const fallbackType = useMemo(() => defaultSegmentType(segments), [segments])
  const selectedType = activeType ?? fallbackType

  if (assessmentId == null) {
    return <section className="rounded-lg border border-gray-200 p-6 text-sm text-gray-500">{t('cumulative.notActive')}</section>
  }
  if (query.isError) return <section role="alert" className="rounded-lg border border-red-200 p-6 text-sm text-red-600">{t('cumulative.loadError')}</section>
  if (!detail) return <section role="status" className="rounded-lg border border-gray-200 p-6 text-sm text-gray-500">{t('cumulative.loading')}</section>

  const active = segments.find((segment) => segment.type === selectedType) ?? segments[0]
  // 발행 기준은 마감이 아니라 두 세그먼트의 종결이다.
  const reportReady = detail.assessment.status === 'RESOLVED' || detail.assessment.readyToResolve

  return (
    <section className="space-y-4" data-testid="cumulative-assessment-detail">
      {/* 범위는 두 세그먼트의 공통 사실이라 탭 밖에 둔다. */}
      <header className="flex items-start justify-between gap-3 rounded-lg border border-gray-200 bg-white p-4">
        <div>
          <h2 className="font-semibold text-gray-900">{t('cumulative.title')}</h2>
          <p className="mt-1 text-sm text-gray-500">{scopeText(detail.assessment, t)}</p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
        {/*
          마감은 교사의 명시적 조작이다. 두 세그먼트가 끝나도 누적 테스트는 열린 채로 남고,
          이 버튼을 눌러야 리포트가 발행되고 원생의 다음 레슨이 열린다.
        */}
        {detail.assessment.status === 'ACTIVE' ? (
          <button
            type="button"
            data-testid="cumulative-resolve-btn"
            disabled={!detail.assessment.readyToResolve || resolving}
            title={detail.assessment.readyToResolve ? undefined : t('cumulative.resolveDisabledHint')}
            onClick={() => setResolveOpen(true)}
            className="shrink-0 rounded-md bg-primary-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-40"
          >
            {t('cumulative.resolve')}
          </button>
        ) : (
          <span className="shrink-0 text-xs font-medium text-gray-500" data-testid="cumulative-resolved-at">
            {t('cumulative.resolvedAt', { at: formatKst(detail.assessment.resolvedAt) })}
          </span>
        )}
        {/*
          리포트는 마감이 아니라 두 세그먼트가 끝나는 순간 발행·동결된다(ADR 0012, 2026-09-21 개정).
          그래서 마감 전에도 확정된 리포트를 보고 마감 여부를 판단할 수 있다.
        */}
        <button
          type="button"
          data-testid="cumulative-report-btn"
          disabled={!reportReady}
          title={reportReady ? undefined : t('cumulative.reportPendingHint')}
          onClick={() => window.open(
            `/admin/coaching/cumulative-report/${learnerId}/${detail.assessment.assessmentId}`,
            '_blank',
            'noopener,noreferrer',
          )}
          className="shrink-0 rounded-md border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
        >
          {t('cumulative.openReport')}
        </button>
        </div>
      </header>

      {resolveError ? (
        <p role="alert" className="rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-600">
          {resolveError}
        </p>
      ) : null}

      <ConfirmDialog
        open={resolveOpen}
        tone="primary"
        title={t('cumulative.resolveConfirmTitle')}
        message={t('cumulative.resolveConfirmMessage')}
        confirmLabel={t('cumulative.resolve')}
        cancelLabel={t('cumulative.resolveCancel')}
        pending={resolving}
        onCancel={() => setResolveOpen(false)}
        onConfirm={() => { void resolve() }}
      />

      {/*
        탭은 한 번에 하나만 보여주므로 "둘 다 끝나야 다음 레슨이 열린다"는 사실을 가린다.
        라벨이 상태를 함께 이고 가서 클릭 없이도 남은 일이 보이게 한다.
      */}
      <div
        role="tablist"
        aria-label={t('cumulative.tabsLabel')}
        data-testid="cumulative-segment-tabs"
        className="inline-flex items-center rounded-md border border-gray-200 bg-gray-50 p-0.5"
      >
        {segments.map((segment) => {
          const isActive = segment.type === active?.type
          return (
            <button
              key={segment.type}
              type="button"
              role="tab"
              id={`cumulative-tab-${segment.type}`}
              aria-selected={isActive}
              aria-controls={`cumulative-panel-${segment.type}`}
              onClick={() => setActiveType(segment.type)}
              className={clsx(
                'rounded-[5px] px-3 py-1.5 text-xs font-semibold transition-colors',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/50',
                isActive
                  ? 'bg-white text-gray-900 shadow-sm ring-1 ring-gray-200'
                  : 'text-gray-500 hover:text-gray-900',
              )}
            >
              {t(`cumulative.segment.${segment.type}`)}
              <span className="ml-1.5 font-normal text-gray-400">
                · {t(`cumulative.status.${segment.status}`)}
              </span>
            </button>
          )
        })}
      </div>

      {active ? (
        <div
          role="tabpanel"
          id={`cumulative-panel-${active.type}`}
          aria-labelledby={`cumulative-tab-${active.type}`}
        >
          {active.type === 'MATH_HEAD' ? (
            <MathHeadPanel
              learnerId={learnerId}
              assessmentId={assessmentId}
              segment={active}
              onChanged={load}
            />
          ) : (
            <ChapterPanel
              learnerId={learnerId}
              assessmentId={assessmentId}
              segment={active}
              onChanged={load}
            />
          )}
        </div>
      ) : null}
    </section>
  )
}

interface PanelProps {
  learnerId: number
  assessmentId: number
  segment: CumulativeSegmentHistoryView
  onChanged: () => Promise<void>
}

function useExemption({ learnerId, assessmentId, segment, onChanged }: PanelProps) {
  const [reason, setReason] = useState('')
  const shortage = segment.availability
  const canExempt = shortage?.availableCount === 0 && !['COMPLETED', 'EXEMPTED'].includes(segment.status)
  const exempt = async () => {
    await cumulativeAssessmentAdminApi.exempt(learnerId, assessmentId, segment.type, reason)
    setReason('')
    await onChanged()
  }
  return { reason, setReason, canExempt, exempt }
}

/** 상태는 탭 라벨이 이미 보여주므로 패널 헤더는 부족·면제 안내만 맡는다. */
function PanelNotices({ segment, t }: { segment: CumulativeSegmentHistoryView; t: ReturnType<typeof useTranslation>['t'] }) {
  const shortage = segment.availability
  return (
    <>
      {shortage && shortage.missingCount > 0 ? (
        <p className="text-sm text-amber-700">
          {t('cumulative.shortage', {
            targetCount: shortage.targetCount,
            availableCount: shortage.availableCount,
            missingCount: shortage.missingCount,
          })}
        </p>
      ) : null}
      {segment.exemptionReason ? (
        <p className="text-sm text-gray-500">{segment.exemptionReason}</p>
      ) : null}
    </>
  )
}

function ExemptionControls({
  t,
  reason,
  setReason,
  onExempt,
}: {
  t: ReturnType<typeof useTranslation>['t']
  reason: string
  setReason: (value: string) => void
  onExempt: () => Promise<void>
}) {
  return (
    <>
      <input
        value={reason}
        onChange={(event) => setReason(event.target.value)}
        placeholder={t('cumulative.exemptionReason')}
        className="rounded border px-3 py-2 text-sm"
      />
      <button
        type="button"
        disabled={!reason.trim()}
        onClick={() => void onExempt()}
        className="rounded border px-3 py-2 text-sm disabled:opacity-50"
      >
        {t('cumulative.exempt')}
      </button>
    </>
  )
}

/**
 * 레슨 테스트 — 회차는 활성화 때 자동으로 만들어진다(ADR 0011).
 * 교사가 회차를 만들거나 고를 일이 없으므로 생성 버튼도 회차 이력도 두지 않는다.
 */
function ChapterPanel(props: PanelProps) {
  const { learnerId, assessmentId, segment, onChanged } = props
  const { t } = useTranslation('coaching')
  const { reason, setReason, canExempt, exempt } = useExemption(props)
  const attempt = segment.chapterAttempts.at(-1) ?? null

  return (
    <article className="space-y-3">
      <PanelNotices segment={segment} t={t} />
      {attempt ? (
        <TestCoachingDetail
          learnerId={learnerId}
          selection={{
            kind: 'CUMULATIVE_ASSESSMENT',
            chapterOrdinal: attempt.chapterOrdinal,
            scopeContentId: attempt.scopeContentId,
            cumulativeAssessmentId: assessmentId,
          }}
          testIdOverride={attempt.testId}
          allowRetake={false}
          showContentManagement={false}
          showReportButton={false}
          onChanged={() => { void onChanged() }}
        />
      ) : (
        <p className="rounded-lg border border-gray-200 bg-white p-4 text-sm text-gray-500">
          {t('cumulative.chapterAttemptPending')}
        </p>
      )}
      {canExempt ? (
        <div className="flex flex-wrap gap-2">
          <ExemptionControls t={t} reason={reason} setReason={setReason} onExempt={exempt} />
        </div>
      ) : null}
    </article>
  )
}

/**
 * Math Head segment: BIGGO run history + staff actions.
 * [재시험 생성] when there is no run or the latest run is terminal; [취소(사유)] on a READY/IN_PROGRESS run;
 * [면제] only when the scope has no World Mapping (availableCount === 0). Scores are staff-visible.
 */
function MathHeadPanel(props: PanelProps) {
  const { learnerId, assessmentId, segment, onChanged } = props
  const { t } = useTranslation('coaching')
  const { reason, setReason, canExempt, exempt } = useExemption(props)
  const [cancelTarget, setCancelTarget] = useState<MathHeadRunSummaryView | null>(null)
  const [cancelReason, setCancelReason] = useState('')
  const [pending, setPending] = useState(false)
  const [actionError, setActionError] = useState<string | null>(null)

  const runs = segment.mathHeadRuns
  const latest = runs.length > 0 ? runs[runs.length - 1] : null
  const activeRun = latest && (latest.status === 'READY' || latest.status === 'IN_PROGRESS') ? latest : null
  const canRetest = activeRun == null && segment.status !== 'EXEMPTED'

  const retest = async () => {
    setActionError(null)
    try {
      await cumulativeAssessmentAdminApi.createMathHeadRetest(learnerId, assessmentId)
      await onChanged()
    } catch (error) {
      setActionError(getApiErrorMessage(error, t))
    }
  }

  const confirmCancel = async () => {
    if (!cancelTarget || !cancelReason.trim()) return
    setPending(true)
    setActionError(null)
    try {
      await cumulativeAssessmentAdminApi.cancelMathHeadRun(learnerId, assessmentId, cancelTarget.runId, cancelReason.trim())
      setCancelTarget(null)
      setCancelReason('')
      await onChanged()
    } catch (error) {
      setActionError(getApiErrorMessage(error, t))
    } finally {
      setPending(false)
    }
  }

  return (
    <article className="rounded-lg border border-gray-200 bg-white p-4" data-testid="math-head-panel">
      <PanelNotices segment={segment} t={t} />
      {runs.length === 0 ? (
        <p className="mt-3 text-sm text-gray-400">{t('cumulative.run.empty')}</p>
      ) : (
        <ul className="mt-3 space-y-2 text-sm text-gray-600">
          {runs.map((run) => <MathHeadRunRow key={run.runId} run={run} t={t} />)}
        </ul>
      )}
      {actionError ? <p role="alert" className="mt-3 text-sm text-red-600">{actionError}</p> : null}
      <div className="mt-4 flex flex-wrap gap-2">
        {canRetest ? (
          <button type="button" onClick={() => void retest()} className="rounded bg-indigo-600 px-3 py-2 text-sm text-white">
            {t('cumulative.createRetest')}
          </button>
        ) : null}
        {activeRun ? (
          <button
            type="button"
            onClick={() => { setCancelReason(''); setCancelTarget(activeRun) }}
            className="rounded border border-red-300 px-3 py-2 text-sm text-red-700"
          >
            {t('cumulative.cancelRun')}
          </button>
        ) : null}
        {canExempt ? <ExemptionControls t={t} reason={reason} setReason={setReason} onExempt={exempt} /> : null}
      </div>
      <ConfirmDialog
        open={cancelTarget != null}
        title={t('cumulative.cancelDialogTitle')}
        message={t('cumulative.cancelDialogMessage')}
        confirmLabel={t('cumulative.cancelConfirm')}
        cancelLabel={t('cumulative.cancelCancel')}
        pending={pending}
        confirmDisabled={!cancelReason.trim()}
        onCancel={() => setCancelTarget(null)}
        onConfirm={() => void confirmCancel()}
      >
        <label className="block text-sm text-gray-700">
          {t('cumulative.cancelReasonLabel')}
          <textarea
            value={cancelReason}
            onChange={(event) => setCancelReason(event.target.value)}
            placeholder={t('cumulative.cancelReasonPlaceholder')}
            maxLength={1000}
            rows={3}
            className="mt-1 w-full rounded border px-3 py-2 text-sm"
          />
        </label>
      </ConfirmDialog>
    </article>
  )
}

function MathHeadRunRow({ run, t }: { run: MathHeadRunSummaryView; t: ReturnType<typeof useTranslation>['t'] }) {
  const hasResult = run.kind === 'BIGGO' && run.status === 'COMPLETED'
  return (
    <li className="rounded border border-gray-100 p-3" data-testid={`math-head-run-${run.runId}`}>
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <span className="font-medium text-gray-900">{t('cumulative.round', { round: run.roundNo })}</span>
        <span>· {t(`cumulative.run.kind.${run.kind}`)}</span>
        <span>· {t(`cumulative.run.status.${run.status}`)}</span>
        <span className="text-xs text-gray-400">({t(`cumulative.run.trigger.${run.triggerType}`)})</span>
      </div>
      <div className="mt-1 text-xs text-gray-500">
        {run.startedAt ? <span className="mr-3">{t('cumulative.run.startedAt')} {formatKst(run.startedAt)}</span> : null}
        {run.completedAt ? <span className="mr-3">{t('cumulative.run.completedAt')} {formatKst(run.completedAt)}</span> : null}
        {run.cancelledAt ? <span className="mr-3">{t('cumulative.run.cancelledAt')} {formatKst(run.cancelledAt)}</span> : null}
      </div>
      {/* Math Head Adaptive Level 은 BIGGO 가 임베드에서도 노출하지 않는 내부 좌표라 표시하지 않는다(ADR 0009 개정). */}
      {hasResult ? (
        <div className="mt-1 text-xs text-gray-700">
          <span className="mr-3 font-semibold">{t('cumulative.run.score')} {run.score ?? '—'}</span>
          <span>
            {t('cumulative.run.counts', {
              correct: run.correctCount ?? 0,
              wrong: run.wrongCount ?? 0,
              timeout: run.timeoutCount ?? 0,
              answered: run.answeredCount ?? 0,
            })}
          </span>
        </div>
      ) : null}
      {run.measurement?.status === 'UNAVAILABLE' ? (
        <p role="status" className="mt-2 text-sm text-muted-foreground">{t('cumulative.run.measurement.checkFailed')}</p>
      ) : run.measurement ? (
        <div role="status" className="mt-2 space-y-1 text-sm text-muted-foreground">
          <p>{t(run.measurement.retestRecommended ? 'cumulative.run.measurement.retest' : 'cumulative.run.measurement.unavailable')}</p>
          <p>{t('cumulative.run.measurement.missing', {
            competencies: run.measurement.missingCompetencies.map((key) => t(`cumulative.run.measurement.competencies.${key}`)).join(', '),
          })}</p>
        </div>
      ) : null}
      {run.cancelReason ? (
        <p className="mt-1 text-xs text-gray-500">{t('cumulative.run.cancelReason', { reason: run.cancelReason })}</p>
      ) : null}
      {run.lastSyncError && run.status !== 'COMPLETED' && run.status !== 'CANCELLED' ? (
        <p className="mt-1 text-xs text-amber-700">{t('cumulative.run.syncError', { error: run.lastSyncError })}</p>
      ) : null}
    </li>
  )
}
